import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initPush } from "@/lib/push";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const pushInit = useRef(false);

  // setState only happens inside the promise callback (async), never synchronously.
  const refresh = useCallback(() => {
    if (!user) return;
    Promise.all([
      api.get("/notifications"),
      api.get("/notifications/unread-count"),
    ])
      .then(([list, cnt]) => {
        setItems(Array.isArray(list.data) ? list.data : []);
        setUnread(cnt.data?.count || 0);
      })
      .catch(() => {});
  }, [user]);

  const markRead = useCallback((id) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    api.post(`/notifications/${id}/read`).catch(() => {});
  }, []);

  const markAll = useCallback(() => {
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setUnread(0);
    api.post("/notifications/read-all").catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    if (!user) return undefined;
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [user, refresh]);

  // Register native push once per logged-in session (no state changes here).
  useEffect(() => {
    if (user && !pushInit.current) {
      pushInit.current = true;
      initPush(() => refresh());
    }
    if (!user) pushInit.current = false;
  }, [user, refresh]);

  // Exposed values are gated by auth so we never need to clear state on logout.
  const value = {
    items: user ? items : [],
    unread: user ? unread : 0,
    refresh,
    markRead,
    markAll,
  };

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationsProvider");
  return ctx;
};
